import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import { salesExpenseService } from '../../services/salesExpenseService';
import { formatCurrency } from '../../components/CurrencyTextField';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  EXPENSE_STATUS_META,
  SalesCard,
  SalesEmptyState,
  SalesPageHeader,
  SalesStatCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesRowHoverSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const SalesExpenses = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [canApprove, setCanApprove] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesExpenseService.getAll({
        status: status || undefined,
        limit: 100,
      });
      setRows(res.data || []);
      setCanApprove(Boolean(res.can_approve));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => ({
    pending: rows.filter((r) => r.status === 'pending').length,
    approved: rows.filter((r) => r.status === 'approved').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
    paid: rows.filter((r) => r.status === 'paid').length,
  }), [rows]);

  const submitterName = (row) =>
    [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || '—';

  return (
    <Box>
      <SalesPageHeader
        title="Expenses"
        subtitle={
          canApprove
            ? 'Review sales expense reimbursements and track approval status.'
            : 'Submit travel and out-of-pocket expenses with a receipt photo.'
        }
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/sales/expenses/new')}
              sx={salesPrimaryButtonSx}
            >
              New Expense
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <SalesStatCard label="Pending" value={stats.pending} />
        <SalesStatCard label="Approved" value={stats.approved} />
        <SalesStatCard label="Rejected" value={stats.rejected} />
        <SalesStatCard label="Paid" value={stats.paid} />
      </Box>

      <SalesCard>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <SalesEmptyState
            icon={<ReceiptLongOutlined />}
            title="No expenses yet"
            description="Submit a receipt photo with amount and job/notes to start a reimbursement."
            actionLabel="New Expense"
            onAction={() => navigate('/sales/expenses/new')}
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={salesTableHeadSx}>Submitted</TableCell>
                  {canApprove && <TableCell sx={salesTableHeadSx}>Rep</TableCell>}
                  <TableCell sx={salesTableHeadSx}>Receipt date</TableCell>
                  <TableCell sx={salesTableHeadSx}>Amount</TableCell>
                  <TableCell sx={salesTableHeadSx}>Status</TableCell>
                  <TableCell sx={salesTableHeadSx}>Job/Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={salesRowHoverSx}
                    onClick={() => navigate(`/sales/expenses/${row.id}`)}
                  >
                    <TableCell>{row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}</TableCell>
                    {canApprove && <TableCell>{submitterName(row)}</TableCell>}
                    <TableCell>{row.receipt_date ? formatDateDisplay(row.receipt_date) : '—'}</TableCell>
                    <TableCell>{row.amount != null ? formatCurrency(row.amount) : '—'}</TableCell>
                    <TableCell>
                      <SalesStatusChip status={row.status} metaMap={EXPENSE_STATUS_META} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                        {row.notes || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SalesCard>
    </Box>
  );
};

export default SalesExpenses;
